import { Suspense } from 'react';

import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import pendingStyles from '../../../pageStructure/mainContentPending/MainContentPending.module.css';
import { TDocumentRequirements } from '../ApplyFlexiLoanFormView';
import { DocumentRequirements } from './documentRequirements/DocumentRequirements';
import styles from './documentRequirements/DocumentRequirements.module.css';
import { DocumentRequirementsDE } from './documentRequirements/DocumentRequirementsDE';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { UPLOAD_FLEXI_LOAN_DOCUMENT_REQUIREMENT } from '../../ApplyFlexiLoanView.actions';

const mapStateToProps = ({ applyFlexiLoanView }: RootState) => ({
    approvalRequirementsLoading: applyFlexiLoanView.approvalRequirementsLoading,
    submitApplicationLoading: applyFlexiLoanView.submitApplicationLoading
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    uploadDocumentRequirement: (documentType: {
        label: string;
        value: string;
    }) => {
        dispatch({
            type: UPLOAD_FLEXI_LOAN_DOCUMENT_REQUIREMENT,
            documentType
        });
    }
});

const ApprovalRequirements = ({
    submitFailed,
    documentRequirements,
    submitApplicationLoading,
    approvalRequirementsLoading,
    uploadDocumentRequirement
}: {
    submitFailed: boolean;
    documentRequirements: TDocumentRequirements;
} & ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>) => {
    const stateKey = useStateKey();

    const { data: amountRequested } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        select: ({ data }) => {
            const requestedProduct = data!.requested_products![0];
            if (requestedProduct.requested_cash_amount !== null) {
                return requestedProduct.requested_cash_amount;
            } else {
                return requestedProduct.amount;
            }
        }
    });

    const isRepaymentReductionApplication = amountRequested === 0;

    return (
        <div data-testid="document-upload">
            <h4>
                {isRepaymentReductionApplication ? (
                    <FormattedMessage
                        id="_.apply_flexi_loan.documentsTitleRepaymentReduction"
                        defaultMessage="Thanks for applying to reduce your monthly repayments. We still need..."
                    />
                ) : (
                    <FormattedMessage
                        id="_.apply_flexi_loan.documentsTitle"
                        defaultMessage="Thanks for applying for an additional {amount, number, :: currency/GBP}. We need some more information from you before we can make a decision"
                        values={{
                            amount: amountRequested
                        }}
                    />
                )}
            </h4>

            {approvalRequirementsLoading ? (
                <Loading />
            ) : (
                <Suspense fallback={<Loading />}>
                    <DEOnly>
                        <DocumentRequirementsDE
                            documentRequirements={documentRequirements}
                            uploadDocumentRequirement={
                                uploadDocumentRequirement
                            }
                            hasSubmitFailed={submitFailed}
                        />
                    </DEOnly>
                    <UKOnly>
                        <DocumentRequirements
                            documentRequirements={documentRequirements}
                            uploadDocumentRequirement={
                                uploadDocumentRequirement
                            }
                            hasSubmitFailed={submitFailed}
                        />
                    </UKOnly>
                </Suspense>
            )}

            <div className="flex justify-center">
                <Button
                    data-testid="submit-application"
                    type="submit"
                    loading={
                        approvalRequirementsLoading || submitApplicationLoading
                    }
                >
                    <FormattedMessage
                        id="_.apply_flexi_loan.submitCTA"
                        defaultMessage="Submit application"
                    />
                </Button>
            </div>
        </div>
    );
};

const Loading = () => (
    <div className={styles.documentRequirement}>
        <div className={pendingStyles.pendingItem} />
    </div>
);

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ApprovalRequirements);
